import React, { useState } from "react";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { baseUrl } from "../../comman/config";
import { openConsumptionReport, trailingTwoYearsBeforeSession } from "../../comman/checkRas";
import { DateSessionFilter } from "../../components/Filter/DateSessionFilter";
import Loader from "../../components/Loader";
import NoRecordsFound from "../../components/NoRecordsFound";
import { ItemNameStacked } from '../../components/ItemName';
import { isReadymadeNos } from '../../comman/methods';

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

// historical_data.fg_consumption_per_person is stored in kg; this screen enters gms.
const kgToGms = (v) => (v == null || v === "" ? "" : num(v) * 1000);
const gmsToKg = (v) => (v === "" || v == null ? "" : num(v) / 1000);

// Capped precision for the two entered columns. Values are rounded here, and it is the rounded
// value that gets saved -- so every downstream calculation (Production Estimation's historical
// columns and the RM <-> FG / Person conversions it drives) works from exactly what's on screen,
// not from a longer number hidden behind the display.
// The kg -> gms conversion also produces float noise (0.02015 * 1000 = 20.150000000000002);
// rounding clears that up as a side effect.
// Density is FIVE, not three. The other two columns belong to historical_data and are this
// screen's own, but Density is meal_master.avg_density -- the very field the Meal Master form
// keeps at five decimals -- so rounding it to what the Consumption Report happens to DISPLAY
// would quietly shorten the stored number every time this screen saved.
const DECIMALS = { fg_consumption_per_person: 2, rm_to_fg_ratio: 3, avg_density: 5 };
const roundTo = (v, d) => {
  if (v === "" || v == null) return "";
  const n = parseFloat(v);
  return isNaN(n) ? "" : parseFloat(n.toFixed(d));
};
const roundField = (field, v) => roundTo(v, DECIMALS[field] ?? 2);

// A number input takes the mouse wheel as a spinner while it has focus, so scrolling the page
// with the pointer over a field silently retyped it -- and these figures are saved onto
// meal_master, where four more columns are derived from the density. Dropping focus on the wheel
// is what the other screens here do (Recipe Planning, Kit Requisition, Meal Master), so the page
// scrolls and the figure stands.
const blockWheel = (e) => e.target.blur();

// The other half of the spinner. Blocking the wheel leaves the up and down arrows, which step a
// number input just the same -- and an arrow reaches the field WITHOUT the pointer being over it,
// so tabbing along a row and pressing Down to move on retyped the cell just left. All three
// columns here take decimals, so only the arrows are blocked.
const blockArrowKeys = (e) => {
  if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
};

function HistoricalData() {
  const [date, setDate] = useState("");
  const [session, setSession] = useState("");
  const [mealList, setMealList] = useState([]);
  const [checked, setChecked] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Triggered by the DateSessionFilter "Submit" button.
  const handleFilterChange = async (selectedDate, selectedSession) => {
    const sessionId = selectedSession?.value;
    if (!selectedDate || !sessionId) {
      toast.error("Please select date and session");
      return;
    }

    setDate(selectedDate);
    setSession(selectedSession);
    setLoading(true);
    try {
      const response = await axios.get(
        `${baseUrl}/historical_data/item_list?date=${selectedDate}&session=${sessionId}`
      );
      const data = response.data.data ?? [];
      // Prefill from historical_data when present, otherwise blank.
      setMealList(
        data.map((item) => ({
          menu_item_id: item.menu_item_id,
          item_name: item.item_name,
          eng_name: item.eng_name,
          // The comma list, read through hasMethod -- never compared to a number. See
          // comman/methods.js for what a bare === does to an item measured that way second.
          method: item.method,
          fg_consumption_per_person:
            item.fg_consumption_per_person != null
              ? roundField("fg_consumption_per_person", kgToGms(item.fg_consumption_per_person))
              : "",
          // A Readymade Nos item is counted in whole pieces: the raw material IS the finished
          // piece, so its RM to FG Ratio is 1, and Density -- "Density or 1 Piece (Kgs)" on Meal
          // Master -- is 1 with it. Both are fixed rather than defaulted, so the pair cannot be
          // typed apart, and both save exactly as any other row's would.
          rm_to_fg_ratio: isReadymadeNos(item)
            ? 1
            : (item.rm_to_fg_ratio != null ? roundField("rm_to_fg_ratio", item.rm_to_fg_ratio) : ""),
          // Straight off meal_master, not historical_data -- this column edits the Meal Master
          // figure in place, so it opens on whatever that screen last saved.
          avg_density: isReadymadeNos(item)
            ? 1
            : (item.avg_density != null ? roundField("avg_density", item.avg_density) : ""),
        }))
      );
      setChecked(new Set());
      setSubmitted(true);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load meal items");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (index, field, value) => {
    setMealList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Rounded on blur rather than on every keystroke: a number input reports "" for a half-typed
  // "2.1", so rounding in onChange would fight the user mid-entry.
  // Bounds for the two entered columns: FG / Person is grams per person and the RM to FG ratio is
  // a multiplier, and for both a value past 999 is a typo rather than a measurement. The maximums
  // carry the same decimals the columns round to, so a clamped value is one the column can hold.
  // Clamped on blur rather than while typing, so a value on its way to 100 is not rewritten under
  // the cursor at "1".
  const LIMITS = {
    fg_consumption_per_person: { min: 0, max: 999.99 },
    rm_to_fg_ratio: { min: 0, max: 999.999 },
    avg_density: { min: 0, max: 999.99999 },
  };
  // The ceiling on FG / Person says "past 999 is a typo rather than a measurement", which is true
  // of grams per person and false of a count of pieces: a Readymade Nos item is planned in whole
  // pav, and 18600 of them is a number somebody means. So that row keeps the floor and loses the
  // ceiling. The other two columns are fixed at 1 for such a row and never reach a clamp.
  const limitFor = (field, row) =>
    (field === "fg_consumption_per_person" && isReadymadeNos(row))
      ? { min: LIMITS[field].min }
      : LIMITS[field];
  const clampField = (field, value, row) => {
    const limit = limitFor(field, row);
    if (!limit || value === "" || value == null) return value;
    const n = parseFloat(value);
    if (isNaN(n)) return "";
    const floored = Math.max(n, limit.min);
    return limit.max === undefined ? floored : Math.min(floored, limit.max);
  };

  const handleInputBlur = (index, field) => {
    setMealList((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: roundField(field, clampField(field, updated[index][field], updated[index])),
      };
      return updated;
    });
  };

  const toggleRow = (index) => {
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  };

  const toggleAll = () => {
    setChecked((prev) =>
      prev.size === mealList.length ? new Set() : new Set(mealList.map((_, i) => i))
    );
  };

  // Opens the Consumption Report in a new tab for the checked meal items, covering EVERY
  // occurrence of the event the selected date falls into -- a Paryushan date reports on
  // Paryushan 2023, 2024 and 2025, not just the year selected.
  const handleCheckRas = async () => {
    const selected = mealList.filter((_, i) => checked.has(i));
    if (!selected.length) {
      toast.error("Please select at least one meal item");
      return;
    }
    if (!date) {
      toast.error("Please select date and session");
      return;
    }

    // The two years leading up to the day being recorded, ending the day before it -- the same
    // window Production Estimation's Check RAS uses, and for the same reason: these figures are
    // read as a trend across every past time the items were served, and the day itself has
    // nothing consumed against it yet.
    await openConsumptionReport(
      date,
      selected.map((r) => r.menu_item_id),
      session,
      trailingTwoYearsBeforeSession(date)
    );
  };

  const handleSave = async () => {
    if (!date || !session?.value) {
      toast.error("Please select date and session");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        date,
        session: session.value,
        items: mealList.map((item) => ({
          menu_item_id: item.menu_item_id,
          // Round FIRST, then convert -- what's stored is exactly the 2-decimal gms figure shown.
          fg_consumption_per_person: gmsToKg(roundField("fg_consumption_per_person", item.fg_consumption_per_person)),
          rm_to_fg_ratio: roundField("rm_to_fg_ratio", item.rm_to_fg_ratio),
          // Left blank means "leave the Meal Master figure alone", not "set it to zero". Four
          // meal_master columns are derived from this density, so a blank saved as 0 would
          // silently recompute all of them against the fallback of 1.
          avg_density:
            item.avg_density === "" || item.avg_density == null
              ? null
              : roundField("avg_density", item.avg_density),
        })),
      };
      const response = await axios.post(`${baseUrl}/historical_data/save`, payload);
      if (response.data.status) {
        toast.success(response.data.msg || "Saved successfully");
      } else {
        toast.error(response.data.msg || "Save failed");
      }
    } catch (error) {
      console.error(error);
      toast.error("Save failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="">
      {loading && <Loader />}
      {/* Title and Check Consumption stay on one line, phone included -- flex-nowrap, so it never
          drops below. min-w-0 lets the heading wrap inside its own column rather than pushing the
          button off; shrink-0 keeps the button at its natural width. */}
      <div className="flex flex-nowrap justify-between items-center gap-2 py-3 px-3 sm:px-6">
        <h1 className="min-w-0 text-base sm:text-xl md:text-xl lg:text-xl xl:text-2xl font-bold">
          Historical Data (Average)
        </h1>
        {submitted && mealList.length > 0 && (
          <button
            onClick={handleCheckRas}
            className="shrink-0 whitespace-nowrap px-2 sm:px-3 py-2 bg-gray-500 text-white text-xs sm:text-sm rounded-md hover:bg-gray-600 focus:outline-none focus:ring focus:ring-gray-200"
          >
            Check Consumption
          </button>
        )}
        <Toaster position="top-center" reverseOrder={false} />
      </div>
      <div className="relative shadow-md sm:rounded-lg">
        <div className="flex flex-wrap items-center gap-4 px-4">
          <DateSessionFilter handleFilterChange={handleFilterChange} />
        </div>

        {submitted && (
          <div className="p-4">
            {mealList.length > 0 ? (
              <>
                <table className="w-full text-sm text-left text-gray-500">
                  <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                    <tr>
                      <th scope="col" className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={mealList.length > 0 && checked.size === mealList.length}
                          onChange={toggleAll}
                        />
                      </th>
                      {/* Half the table. w-auto left this competing with two number columns whose
                          inputs were w-full and therefore greedy, so the longest column -- a
                          Gujarati name with its English name after it -- got the least room. The
                          two entry fields are sized to their own values now, and this claims the
                          rest rather than relying on what they leave behind. */}
                      <th scope="col" className="px-2 py-3 w-1/2">
                        Meal Item Name
                      </th>
                      {/* normal-case because the thead is uppercase: without it this reads
                          "FG / PERSON (GMS)" rather than the name the column was asked to carry. */}
                      <th scope="col" className="px-2 py-3 w-28 normal-case">
                        FG / Person (gms)
                      </th>
                      <th scope="col" className="px-2 py-3 w-28">
                        RM to FG Ratio
                      </th>
                      <th scope="col" className="px-2 py-3 w-28">
                        Density
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {mealList.map((item, index) => (
                      <tr
                        key={item.menu_item_id}
                        className={`border-b ${checked.has(index) ? "bg-amber-100" : "bg-white"}`}
                      >
                        <td className="px-3 py-3">
                          <input
                            type="checkbox"
                            checked={checked.has(index)}
                            onChange={() => toggleRow(index)}
                          />
                        </td>
                        <td className="px-2 py-3 font-medium text-gray-900">
                          <ItemNameStacked item={item} />
                        </td>
                        <td className="px-2 py-3">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            // No ceiling on a count of pieces -- see limitFor.
                            max={isReadymadeNos(item) ? undefined : "999.99"}
                            className="block w-24 p-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-yellow-50 focus:ring-blue-500 focus:border-blue-500"
                            value={item.fg_consumption_per_person}
                            onWheel={blockWheel}
                            onKeyDown={blockArrowKeys}
                            onChange={(e) =>
                              handleInputChange(index, "fg_consumption_per_person", e.target.value)
                            }
                            onBlur={() => handleInputBlur(index, "fg_consumption_per_person")}
                          />
                        </td>
                        <td className="px-2 py-3">
                          <input
                            type="number"
                            step="0.001"
                            min="0"
                            max="999.999"
                            // Fixed at 1 on a Readymade Nos row: the raw material IS the finished
                            // piece. Read-only so the pair cannot be typed apart, and greyed so
                            // the row says why it will not take a number.
                            readOnly={isReadymadeNos(item)}
                            title={isReadymadeNos(item) ? 'Readymade Nos: a piece is a piece, so this is always 1' : undefined}
                            className={`block w-28 p-2 text-sm text-gray-900 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 ${isReadymadeNos(item) ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-yellow-50'}`}
                            value={item.rm_to_fg_ratio}
                            onWheel={blockWheel}
                            onKeyDown={blockArrowKeys}
                            onChange={(e) =>
                              handleInputChange(index, "rm_to_fg_ratio", e.target.value)
                            }
                            onBlur={() => handleInputBlur(index, "rm_to_fg_ratio")}
                          />
                        </td>
                        <td className="px-2 py-3">
                          <input
                            type="number"
                            step="0.00001"
                            min="0"
                            max="999.99999"
                            // Fixed at 1 on a Readymade Nos row: the raw material IS the finished
                            // piece. Read-only so the pair cannot be typed apart, and greyed so
                            // the row says why it will not take a number.
                            readOnly={isReadymadeNos(item)}
                            title={isReadymadeNos(item) ? 'Readymade Nos: a piece is a piece, so this is always 1' : undefined}
                            className={`block w-28 p-2 text-sm text-gray-900 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 ${isReadymadeNos(item) ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-yellow-50'}`}
                            value={item.avg_density}
                            onWheel={blockWheel}
                            onKeyDown={blockArrowKeys}
                            onChange={(e) =>
                              handleInputChange(index, "avg_density", e.target.value)
                            }
                            onBlur={() => handleInputBlur(index, "avg_density")}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex justify-end items-center gap-3 p-4">
                  <span className="text-sm text-gray-600 mr-auto">
                    Selected: <span className="font-semibold">{checked.size}</span> of{" "}
                    {mealList.length}
                  </span>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200"
                  >
                    Save
                  </button>
                </div>
              </>
            ) : (
              <NoRecordsFound />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default HistoricalData;
